import React from 'react';

import { Badge, Box, Card, CardBody, Flex, HStack, VStack, useDisclosure } from '@chakra-ui/react';
import { useRouter } from 'next/navigation';
import { FaStar } from 'react-icons/fa';
import { HiOutlineCheckCircle } from 'react-icons/hi';

import { PrimaryButton } from '@components/custom-buttons';
import { CustomModal } from '@components/custom-modal';
import { CustomText } from '@components/Custom-text';
import { UserType } from '@enums/index';
import { useAppDispatch } from '@hooks/store';
import useAuthSelector from '@hooks/useAuthSelector';
import { setLogout } from '@modules/auth/lib/authSlice';
import useRazorPay from '@modules/pricing/hooks/useRazorPay';
import { paths } from '@routes/path';
import { getFormattedPrice } from '@utils/helpers';

interface PricingCardProps {
    id: string;
    name: string;
    price: string | number;
    actualPrice?: string | number;
    title: string;
    currency: string;
    features: string[];
    showHeader: boolean;
    headerText?: string;
    isAddOn: boolean;
}

interface FeatureProps {
    feature: string;
}

const betaRegex = /<beta>(.+)<\/beta>/;

const Feature = ({ feature }: FeatureProps) => {
    const isBeta = betaRegex.test(feature);
    feature = feature.replace(betaRegex, '$1');
    return (
        <HStack align="flex-start" gap={2}>
            {isBeta ? (
                <Badge
                    backgroundColor="purple.100"
                    color="purple.800"
                    px={2.5}
                    py={0.5}
                    fontWeight="medium"
                    borderRadius={6}
                    textTransform="none"
                    alignSelf="center"
                    fontSize={12}
                >
                    Beta
                </Badge>
            ) : (
                <div>
                    <HiOutlineCheckCircle color="#34C1A6" size={20} />
                </div>
            )}
            <CustomText color="mainText" weight={400} size="sm">
                <span
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: feature }}
                />
            </CustomText>
        </HStack>
    );
};

const PricingCard = ({
    id,
    name,
    price,
    actualPrice,
    title,
    currency,
    features,
    showHeader,
    headerText,
    isAddOn,
}: PricingCardProps) => {
    const { user, isAuthenticated } = useAuthSelector();
    const { isOpen, onClose, onOpen } = useDisclosure();
    const router = useRouter();
    const dispatch = useAppDispatch();
    const { handlePayment, isLoading } = useRazorPay({ id });
    const handleConfirm = () => {
        if (isAuthenticated && user?.userType === UserType.Mentor) {
            dispatch(setLogout());
            router.replace(paths.mentee.auth.signup);
        } else {
            router.replace(paths.mentee.auth.signup);
        }
    };
    return (
        <VStack gap={0} minH={430}>
            {showHeader ? (
                <Box
                    display="flex"
                    minH={12}
                    minW={260}
                    borderTopLeftRadius={12}
                    borderTopRightRadius={12}
                    width="100%"
                    bgColor="primary.dark"
                    justifyContent="center"
                    alignItems="center"
                >
                    <CustomText color="white">{headerText}</CustomText>
                </Box>
            ) : (
                <Box minH={12} width="100%" />
            )}
            <Card
                display="flex"
                variant="elevated"
                width="100%"
                height="100%"
                size="md"
                boxShadow={showHeader ? 'xl' : 'lg'}
                borderWidth={1}
                borderColor="border"
                borderTopLeftRadius={showHeader ? 0 : ''}
                borderTopRightRadius={showHeader ? 0 : ''}
            >
                <CardBody>
                    <VStack width="100%" justifyContent="space-between" height="100%">
                        {isAddOn && (
                            <Box position="absolute" top="-4px" right="0px">
                                <Badge colorScheme="green">Add On</Badge>
                            </Box>
                        )}
                        <VStack align="center" justify="center" width="100%">
                            <VStack width="100%" gap={2} mb={2}>
                                <CustomText color="subText" weight={500} size="lg">
                                    {name}
                                </CustomText>
                                <div>
                                    {actualPrice ? (
                                        <>
                                            <CustomText
                                                as="span"
                                                color="mainText"
                                                weight={500}
                                                size="xl"
                                                textDecoration="line-through"
                                            >
                                                {getFormattedPrice(actualPrice, currency)}
                                            </CustomText>
                                            &nbsp;&nbsp;
                                        </>
                                    ) : null}
                                    <CustomText as="span" color="mainText" weight={700} size="2xl">
                                        {getFormattedPrice(price, currency)}
                                    </CustomText>
                                </div>
                                <CustomText
                                    textAlign="center"
                                    color="mainText"
                                    weight={400}
                                    size="sm"
                                >
                                    <span
                                        // eslint-disable-next-line react/no-danger
                                        dangerouslySetInnerHTML={{ __html: title }}
                                    />
                                </CustomText>
                                <Flex alignItems="center" marginTop={2}>
                                    <FaStar color="orange" />
                                    <CustomText
                                        color="mainText"
                                        weight={500}
                                        paddingLeft={2}
                                        paddingRight={2}
                                    >
                                        Key Benefits
                                    </CustomText>
                                    <FaStar color="orange" />
                                </Flex>
                            </VStack>
                            <VStack width="100%" gap={6} align="flex-start" h="auto" mb={12}>
                                {features &&
                                    features.length > 0 &&
                                    features.map((feature, index) => (
                                        <Feature key={`key ${index}`} feature={feature} />
                                    ))}
                            </VStack>
                        </VStack>
                        <VStack alignSelf="flex-end" width="100%" gap={2} mt={2}>
                            <PrimaryButton
                                width="100%"
                                buttonLabel={isAddOn ? 'Buy ADD ON' : 'Buy Now'}
                                type="button"
                                isLoading={isLoading}
                                onClick={() => {
                                    if (!isAuthenticated || user?.userType === UserType.Mentor) {
                                        onOpen();
                                    } else {
                                        handlePayment();
                                    }
                                }}
                            />
                            {/* <OutlineButton
                                width="100%"
                                type="button"
                                buttonLabel="Chat to sales"
                                onClick={() => {
                                    router.push(paths.general.contact);
                                }}
                            /> */}
                        </VStack>
                    </VStack>
                </CardBody>
            </Card>
            <CustomModal
                confirmBtnText="Sign Up"
                cancelBtnText="Cancel"
                cancelBtnColorScheme="gray"
                confirmBtnColorScheme="blue"
                isOpen={isOpen}
                onClose={onClose}
                onConfirmation={handleConfirm}
                onCancel={onClose}
                title={null}
                body="Please sign up as a mentee to buy a plan"
                isLoading={false}
                bodyFontSize="2xl"
                hideCloseBtn
                textAlign="center"
                showIcon
                cancelBtnTextColor="mainText"
            />
        </VStack>
    );
};

export default PricingCard;
